/** Constantes compartidas — Materialidad (Trámites). Separable a otro módulo. */

export const MATERIALIDAD_TAB_FALTANTES = "faltantes";
export const MATERIALIDAD_TAB_CONTRAPARTES = "contrapartes";
export const MATERIALIDAD_TAB_PORTAL = "portal";
export const MATERIALIDAD_TAB_PROYECTOS = "proyectos";
export const MATERIALIDAD_TAB_RESUMEN = "resumen";
export const MATERIALIDAD_TAB_EFOS = "efos";
export const MATERIALIDAD_TAB_CRITERIOS = "criterios";
export const MATERIALIDAD_TAB_CONFIG = "config";

/** Etiqueta UI — bandeja interna del despacho (contraste con Portal de clientes). */
export const MATERIALIDAD_TAB_FALTANTES_LABEL = "Revisión del despacho";
export const MATERIALIDAD_TAB_PORTAL_LABEL = "Portal de clientes";
export const MATERIALIDAD_TAB_EFOS_LABEL = "EFOS / 69-B";

/** Subpestañas dentro de Portal de clientes. */
export const MATERIALIDAD_PORTAL_SUB_ACCESO = "acceso";
export const MATERIALIDAD_PORTAL_SUB_DOCUMENTOS = "documentos";
export const MATERIALIDAD_PORTAL_SUB_PROYECTOS = "proyectos";
export const MATERIALIDAD_PORTAL_SUB_ACCESO_LABEL = "Alta de acceso";
export const MATERIALIDAD_PORTAL_SUB_DOCUMENTOS_LABEL = "Documentos del proveedor";
export const MATERIALIDAD_PORTAL_SUB_PROYECTOS_LABEL = "Proyectos";

export const MATERIALIDAD_PERIOD_MODE_MES = "mes";
export const MATERIALIDAD_PERIOD_MODE_RANGO = "rango";
export const MATERIALIDAD_PERIOD_MODE_EJERCICIO = "ejercicio";

const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

export function currentPeriodKey() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

export function formatPeriodLabel(periodKey) {
  if (!periodKey || periodKey.length !== 7) return periodKey || "—";
  const [y, m] = periodKey.split("-");
  const idx = parseInt(m, 10) - 1;
  return `${MONTH_NAMES[idx] || m} ${y}`;
}

export function defaultPeriodSelection() {
  const year = new Date().getFullYear();
  return {
    mode: MATERIALIDAD_PERIOD_MODE_EJERCICIO,
    period: null,
    periodFrom: `${year}-01`,
    periodTo: `${year}-12`,
    exerciseYear: year,
  };
}

/**
 * Convierte selección UI → campos de periodo en camelCase.
 * Los helpers de materialidadApi.js mapean a period / period_from / period_to del backend.
 * Importante: no devolver snake_case aquí — si no, fetchMaterialidadFaltantes/Resumen
 * ignoran el rango y el API cae al mes actual (CFDI cargados «no se consideran»).
 */
export function buildPeriodQuery(selection) {
  const sel = selection || defaultPeriodSelection();
  if (sel.mode === MATERIALIDAD_PERIOD_MODE_MES) {
    return { period: sel.period || currentPeriodKey() };
  }
  if (sel.mode === MATERIALIDAD_PERIOD_MODE_RANGO || sel.mode === MATERIALIDAD_PERIOD_MODE_EJERCICIO) {
    const from = sel.periodFrom || `${(sel.exerciseYear || new Date().getFullYear())}-01`;
    const to = sel.periodTo || (sel.mode === MATERIALIDAD_PERIOD_MODE_EJERCICIO
      ? `${sel.exerciseYear || new Date().getFullYear()}-12`
      : currentPeriodKey());
    return { periodFrom: from, periodTo: to };
  }
  return { period: currentPeriodKey() };
}

export function formatPeriodSelectionLabel(selection) {
  const sel = selection || defaultPeriodSelection();
  if (sel.mode === MATERIALIDAD_PERIOD_MODE_MES) {
    return formatPeriodLabel(sel.period || currentPeriodKey());
  }
  const from = sel.periodFrom || "—";
  const to = sel.periodTo || "—";
  if (sel.mode === MATERIALIDAD_PERIOD_MODE_EJERCICIO) {
    return `Ejercicio ${sel.exerciseYear || from.slice(0, 4)} · ${formatPeriodLabel(from)} – ${formatPeriodLabel(to)}`;
  }
  return `${formatPeriodLabel(from)} – ${formatPeriodLabel(to)}`;
}

/** Estados operativos (indicador preventivo; no dictamen legal). */
export const ITEM_STATUS_LABELS = {
  pending: "Evidencia insuficiente",
  complete: "Suficiente (revisión interna)",
  waived: "No aplica",
  requested: "Solicitado al cliente",
  rejected: "Con observaciones",
  in_review: "Pendiente revisión profesional",
};

export const ITEM_STATUS_BADGE_CLASS = {
  pending: "bg-amber-50 text-amber-900 border-amber-200",
  complete: "bg-emerald-50 text-emerald-900 border-emerald-200",
  waived: "bg-slate-50 text-slate-600 border-slate-200",
  requested: "bg-sky-50 text-sky-900 border-sky-200",
  rejected: "bg-rose-50 text-rose-900 border-rose-200",
  in_review: "bg-violet-50 text-violet-900 border-violet-200",
};

export const DETECTION_LABELS = {
  ppd_sin_complemento: "PPD sin complemento",
  cfdi_cancelado: "CFDI cancelado",
  proveedor_nuevo: "Proveedor nuevo",
  importe_alto_sin_contrato: "Importe alto",
  giro_inconsistente_emitido: "Giro inconsistente (emitido)",
  giro_inconsistente_recibido: "Compra poco alineada al giro",
};

export const DETECTION_CRITERIA = {
  ppd_sin_complemento: "Factura PPD sin complemento de pago relacionado en Visor SAT.",
  cfdi_cancelado: "CFDI con estado Cancelado en la copia local del Visor.",
  proveedor_nuevo: "Primer CFDI recibido de este RFC en el historial del cliente.",
  importe_alto_sin_contrato:
    "Importe alto: facturas I menos notas de crédito E vigentes del mes (fecha de emisión), sin pagos P ni cancelados; umbral de la empresa (relativo a ingresos del mes o piso/fijo). Sin contrato verificado en expediente.",
  giro_inconsistente_emitido:
    "Clave o descripción del concepto emitido no coincide con actividades de la constancia fiscal (ej. carpintería facturando papelería).",
  giro_inconsistente_recibido:
    "Compra cuyo concepto parece poco alineado al giro (importe relevante). Señal suave; puede ser insumo legítimo.",
};

export const RISK_LABELS = {
  critico: "Crítico",
  alto: "Alto",
  medio: "Medio",
  bajo: "Bajo",
  none: "Sin señal",
};

export const RISK_BADGE_CLASS = {
  critico: "bg-rose-100 text-rose-950 border-rose-300",
  alto: "bg-rose-50 text-rose-900 border-rose-200",
  medio: "bg-amber-50 text-amber-900 border-amber-200",
  bajo: "bg-slate-50 text-slate-600 border-slate-200",
  none: "bg-slate-50 text-slate-500 border-slate-200",
};

/** Filtros del selector de proveedores (despacho). */
export const COUNTERPARTY_RISK_FILTER_ALL = "todos";
export const COUNTERPARTY_RISK_FILTER_ALTO = "alto";
export const COUNTERPARTY_RISK_FILTER_MEDIO = "medio";
export const COUNTERPARTY_RISK_FILTER_BAJO = "bajo";

/** ¿La fila entra en el chip de filtro? «alto» incluye crítico. */
export function counterpartyMatchesRiskFilter(row, filter) {
  const level = String(row?.risk_level || "none");
  if (!filter || filter === COUNTERPARTY_RISK_FILTER_ALL) return true;
  if (filter === COUNTERPARTY_RISK_FILTER_ALTO) {
    return level === "alto" || level === "critico";
  }
  if (filter === COUNTERPARTY_RISK_FILTER_MEDIO) return level === "medio";
  if (filter === COUNTERPARTY_RISK_FILTER_BAJO) {
    return level === "bajo" || level === "none";
  }
  return true;
}

/** Hallazgos que marcan revisión inmediata (independiente del puntaje). */
export const CRITICAL_FLAG_LABELS = {
  efos_riesgo: "Proveedor en listado de riesgo (EFOS)",
  cfdi_cancelado: "CFDI cancelado",
};

export const CRITICAL_BADGE_CLASS = "bg-rose-100 text-rose-950 border-rose-300";
